import { type GpToastProps, useGpToasts } from '@gportal/uikit';

// change the types for easier usage
type Notification = Omit<GpToastProps, 'toastType' | 'text'> & {
  type?: GpToastProps['toastType'];
  message: string;
};

/**
 * Abstracts GpToasts to provide a simple interface for adding and removing notifications
 *
 * Example:
 * ```ts
 * import useNotifications from 'gpcore-uikit/composables/useNotifications';
 *
 * const { addNotification, removeNotification } = useNotifications();
 *
 * const nId = addNotification({
 *  message: 'Hello, world!',
 *  type: 'success',
 * });
 *
 * removeNotification(nId);
 * ```
 */
const useNotifications = () => {
  const { createToast, dismissToast } = useGpToasts();

  /**
   * Add a notification
   *
   * @param n - Notification
   * @returns notification id (toastId) for removal
   *
   * @default type - 'primary'
   */
  const addNotification = ({ type = 'primary', ...n }: Notification) => {
    return createToast({
      ...n,
      toastType: type,
      text: n.message,
    } as GpToastProps);
  };

  /**
   * Remove a notification
   *
   * @param id - notification id (toastId)
   */
  const removeNotification = (id: string) => {
    dismissToast(id);
  };

  return {
    addNotification,
    removeNotification,
  };
};

export default useNotifications;
